// File: src/components/potus/ExecutiveOrderCard.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDate } from '../../utils/constants';
import { getSectorTags, getSectorColor } from '../../utils/sectorAnalyzer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import trumpImage from '../../images/potus/realDonaldTrump.jpeg';

const ExecutiveOrderCard = ({ order }) => {
  const theme = useTheme();

  // Get sector tags from order data or fallback to content analysis
  const getOrderSectorTags = () => {
    // If backend provided sector_tags, use those
    if (order.sector_tags && order.sector_tags.length > 0) {
      return order.sector_tags;
    }
    
    // Fallback: analyze content in frontend (for existing data without tags)
    return getSectorTags(order.title, order.content || '');
  };

  const sectorTags = getOrderSectorTags();

  // Extract category from title or use default
  const getCategory = () => {
    return 'Executive Orders';
  };

  return (
    <Card
      variant="stats"
      sx={{
        mb: 2,
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(19, 47, 76, 0.4)'
          : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        }
      }}
    >
      <CardContent>
        {/* Header with Avatar, Title, and Sector Tags */}
        <Box display="flex" alignItems="flex-start" mb={2} gap={2}>
          <Avatar
            src={trumpImage}
            alt="Donald Trump"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0
            }}
          >
            DT
          </Avatar>
          
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="flex-start"
            width="100%"
            gap={1}
          >
            {/* Left side - Title and metadata */}
            <Box flexGrow={1} minWidth={0}>
              <Typography variant="h6" component="h3" gutterBottom>
                Donald J. Trump
              </Typography>
              
              {/* Executive Order Title */}
              <Typography 
                variant="h6" 
                component="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ 
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {order.title}
              </Typography>
              
              {/* Category and Date */}
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1}
                flexWrap="wrap"
                mb={1}
              >
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontWeight="medium"
                >
                  {getCategory()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  •
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                >
                  {formatDate(order.date)}
                </Typography>
              </Box>
            </Box>

            {/* Right side - Sector Tags */}
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="flex-end"
              gap={0.5}
              flexShrink={0}
            >
              {sectorTags.map((tag, index) => (
                <Chip
                  key={`${tag}-${index}`}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: getSectorColor(tag), // Using imported function
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '22px',
                    minWidth: { xs: '60px', sm: 'auto' },
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Content Preview */}
        <Typography 
          variant="body2" 
          color="text.primary" 
          paragraph
          sx={{
            lineHeight: 1.5,
            mb: 2,
            wordBreak: 'break-word',
            hyphens: 'auto'
          }}
        >
          {order.preview || (order.content?.substring(0, 300) + '...')}
        </Typography>

        {/* Action Button */}
        <Box 
          display="flex" 
          justifyContent="flex-end"
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-end' }
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<OpenInNewIcon />}
            component={Link}
            href={order.source_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              fontWeight: 'bold',
              minHeight: { xs: '40px', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.8125rem' }
            }}
          >
            Read Full Order
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExecutiveOrderCard;